import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import {
  LinkIcon,
  TrashIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import ConfirmationModal from './ConfirmationModal';
import AddRelatedRecordModal from './AddRelatedRecordModal';

// Strip HTML tags from text
function stripHtml(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

interface RelatedRecord {
  id: string;
  relationshipType: 'PARENT_CHILD' | 'RELATED_TO' | 'CROSS_ENTITY';
  createdAt: string;
  TargetCase: {
    id: string;
    number: string;
    shortDescription: string;
    ticketType: string;
    state: string;
    priority: string;
    Customer?: {
      id: string;
      accountName: string;
    };
    Engagement?: {
      id: string;
      name: string;
    };
  };
  User: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface RelatedRecordsTabProps {
  caseId: string;
  caseNumber: string;
  ticketType: string;
  canManage?: boolean;
  onRelatedRecordAdded?: () => void; // Optional callback when related record is added
  onRelatedRecordDeleted?: () => void; // Optional callback when related record is deleted
}

const RelatedRecordsTab: React.FC<RelatedRecordsTabProps> = ({
  caseId,
  caseNumber,
  ticketType,
  canManage = true,
  onRelatedRecordAdded,
  onRelatedRecordDeleted
}) => {
  const queryClient = useQueryClient();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [relationToDelete, setRelationToDelete] = useState<RelatedRecord | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);

  // Fetch related records
  const { data, isLoading, error } = useQuery<{ relationships: RelatedRecord[] }>({
    queryKey: ['related-records', caseId],
    queryFn: async () => {
      const response = await api.get(`/cases/${caseId}/related-records`);
      return response.data;
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (relationId: string) => {
      await api.delete(`/cases/${caseId}/related-records/${relationId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['related-records', caseId] });
      queryClient.invalidateQueries({ queryKey: ['case-activities', caseId] });
      setDeleteModalOpen(false);
      setRelationToDelete(null);
      
      // Call callback if provided
      if (onRelatedRecordDeleted) {
        onRelatedRecordDeleted();
      }
    }
  });

  const handleDeleteClick = (relation: RelatedRecord) => {
    setRelationToDelete(relation);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (relationToDelete) {
      deleteMutation.mutate(relationToDelete.id);
    }
  };

  const getTicketTypeBadge = (type: string) => {
    const styles = {
      INCIDENT: 'bg-red-100 text-red-800',
      PROBLEM: 'bg-orange-100 text-orange-800',
      CHANGE: 'bg-yellow-100 text-yellow-800',
      SERVICE_REQUEST: 'bg-indigo-100 text-indigo-800',
      CASE: 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${styles[type as keyof typeof styles] || 'bg-gray-100 text-gray-800'}`}>
        {type.replace('_', ' ')}
      </span>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const styles = {
      critical_1: 'bg-red-600 text-white',
      high_2: 'bg-orange-500 text-white',
      moderate_3: 'bg-yellow-500 text-white',
      low_4: 'bg-green-500 text-white',
      planning_5: 'bg-blue-500 text-white'
    };

    const labels = {
      critical_1: 'Critical',
      high_2: 'High',
      moderate_3: 'Moderate',
      low_4: 'Low',
      planning_5: 'Planning'
    };

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded ${styles[priority as keyof typeof styles] || 'bg-gray-500 text-white'}`}>
        {labels[priority as keyof typeof labels] || priority}
      </span>
    );
  };

  const getStateColor = (state: string) => {
    const stateColors: Record<string, string> = {
      new: 'text-blue-600',
      in_progress: 'text-yellow-600',
      on_hold: 'text-orange-600',
      resolved: 'text-green-600',
      closed: 'text-gray-600',
      cancelled: 'text-red-600',
      assess: 'text-blue-600',
      authorize: 'text-indigo-600',
      scheduled: 'text-purple-600',
      implement: 'text-yellow-600',
      review: 'text-orange-600'
    };

    return stateColors[state] || 'text-gray-600';
  };

  const getTicketTypeRoute = (ticketType: string): string => {
    const routeMap: Record<string, string> = {
      'INCIDENT': 'incidents',
      'PROBLEM': 'problems',
      'CHANGE': 'changes',
      'SERVICE_REQUEST': 'service-requests',
      'CASE': 'cases'
    };
    return routeMap[ticketType] || `${ticketType.toLowerCase()}s`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading related records...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">Failed to load related records</div>
      </div>
    );
  }

  const relationships = data?.relationships || [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <LinkIcon className="h-5 w-5 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900">
            Related Records ({relationships.length})
          </h3>
        </div>

        {canManage && (
          <button
            onClick={() => setAddModalOpen(true)}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add Related Record
          </button>
        )}
      </div>

      {/* Related Records Table - Tabular Format */}
      {relationships.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <LinkIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No related records</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by adding a related record.
          </p>
          {canManage && (
            <div className="mt-6">
              <button
                onClick={() => setAddModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Related Record
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Number
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  State
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                {canManage && (
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {relationships.map((relation) => (
                <tr key={relation.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getTicketTypeBadge(relation.TargetCase.ticketType)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <a
                      href={`/${getTicketTypeRoute(relation.TargetCase.ticketType)}/${relation.TargetCase.id}`}
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-900"
                    >
                      {relation.TargetCase.number}
                    </a>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{stripHtml(relation.TargetCase.shortDescription)}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      Added by {relation.User.firstName} {relation.User.lastName} on{' '}
                      {new Date(relation.createdAt).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getPriorityBadge(relation.TargetCase.priority)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-medium capitalize ${getStateColor(relation.TargetCase.state)}`}>
                      {relation.TargetCase.state.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {relation.TargetCase.Customer?.accountName || '-'}
                  </td>
                  {canManage && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleDeleteClick(relation)}
                        className="inline-flex items-center p-1.5 border border-transparent rounded text-red-600 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        title="Remove relationship"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {relationToDelete && (
        <ConfirmationModal
          isOpen={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setRelationToDelete(null);
          }}
          onConfirm={handleConfirmDelete}
          title="Remove Related Record"
          message={`Are you sure you want to remove the relationship to ${relationToDelete.TargetCase.number}? This action cannot be undone.`}
          confirmLabel="Remove"
          confirmButtonClass="bg-red-600 hover:bg-red-700 focus:ring-red-500"
          isLoading={deleteMutation.isPending}
        />
      )}

      {/* Add Related Record Modal */}
      <AddRelatedRecordModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        caseId={caseId}
        caseNumber={caseNumber}
        ticketType={ticketType}
        onSuccess={() => {
          if (onRelatedRecordAdded) {
            onRelatedRecordAdded();
          }
        }}
      />
    </div>
  );
};

export default RelatedRecordsTab;
